import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'

// Deep media-pipeline instrumentation, enabled with ?debug=true.
//
// Written for a Safari-only failure where the player reaches `ready` but never
// reaches `loadedmetadata` AND never fires `error` — it just hangs. The normal
// PlaybackCore logging can't distinguish "request never sent", "request sent,
// no bytes", "bytes arrived, demux failed" or "native HLS took over", so this
// dumps the raw element/tech/network state instead of the video.js abstraction.

const enabled = (() => {
  try {
    return new URLSearchParams(window.location.search).get('debug') === 'true'
  } catch {
    return false
  }
})()

const t0 = Date.now()
const stamp = () => `+${String(Date.now() - t0).padStart(6, ' ')}ms`

// Safari's console renders nested objects as a collapsed "Object", and that is
// what lands in the clipboard — a pasted log of `{element: Object}` carries no
// information at all. So everything is flattened into the message string.
const flatten = (o: Record<string, unknown>): string =>
  Object.entries(o)
    .map(([k, v]) => `${k}=${typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`)
    .join(' ')

const log = (label: string, state?: Record<string, unknown>) => {
  if (!enabled) return
  console.log(`[MediaDiag] ${stamp()} ${label}${state ? ' ' + flatten(state) : ''}`)
}

const NETWORK_STATE = ['EMPTY(0)', 'IDLE(1)', 'LOADING(2)', 'NO_SOURCE(3)']
const READY_STATE = ['NOTHING(0)', 'METADATA(1)', 'CURRENT_DATA(2)', 'FUTURE_DATA(3)', 'ENOUGH_DATA(4)']

// Every media event worth seeing. `timeupdate`/`progress` are the noisy ones —
// they're logged but throttled below.
const MEDIA_EVENTS = [
  'loadstart', 'durationchange', 'loadedmetadata', 'loadeddata', 'progress',
  'canplay', 'canplaythrough', 'playing', 'waiting', 'seeking', 'seeked',
  'ended', 'emptied', 'stalled', 'suspend', 'abort', 'error', 'ratechange',
  'volumechange', 'play', 'pause', 'timeupdate', 'encrypted', 'waitingforkey',
]

const NOISY = new Set(['timeupdate', 'progress'])

export function elementState(el: HTMLVideoElement): Record<string, unknown> {
  const buffered: string[] = []
  try {
    for (let i = 0; i < el.buffered.length; i++) {
      buffered.push(`${el.buffered.start(i).toFixed(2)}-${el.buffered.end(i).toFixed(2)}`)
    }
  } catch { /* buffered throws on a torn-down element */ }
  return {
    readyState: READY_STATE[el.readyState] ?? el.readyState,
    networkState: NETWORK_STATE[el.networkState] ?? el.networkState,
    error: el.error ? { code: el.error.code, message: el.error.message } : null,
    currentSrc: el.currentSrc || '(none)',
    srcAttr: el.getAttribute('src') || '(none)',
    paused: el.paused,
    muted: el.muted,
    volume: el.volume,
    currentTime: Number(el.currentTime.toFixed(2)),
    duration: el.duration,
    videoWidth: el.videoWidth,
    videoHeight: el.videoHeight,
    crossOrigin: el.crossOrigin,
    preload: el.preload,
    autoplay: el.autoplay,
    buffered: buffered.join(',') || '(empty)',
    seekable: el.seekable.length,
    inDocument: el.isConnected,
    srcObject: el.srcObject ? String(el.srcObject) : null,
  }
}

// Which playback path actually won. On Safari this is THE question: video.js
// either handed the stream to VHS (MSE, `vhs` present) or let WebKit play the
// HLS natively (no `vhs`, currentSrc is the m3u8 itself, not a blob:).
export function techState(player: Player, el: HTMLVideoElement): Record<string, unknown> {
  try {
    const tech = (player as any).tech?.(true)
    const vhs = tech?.vhs
    // The DOM property, NOT player.currentSrc() — video.js echoes back the
    // source you handed it (always the .m3u8), whereas the element's
    // currentSrc becomes a blob: URL once MSE is actually driving. Only the
    // latter distinguishes VHS from WebKit's native HLS.
    const elSrc = el.currentSrc || ''
    return {
      techName: tech?.name_ ?? '(unknown)',
      usingVHS: Boolean(vhs),
      playbackPath: elSrc.startsWith('blob:') ? 'MSE/VHS' : (elSrc.includes('.m3u8') ? 'NATIVE-HLS' : 'other/none'),
      elCurrentSrc: elSrc || '(none)',
      vjsCurrentSrc: player.currentSrc?.() || '(none)',
      currentType: player.currentType?.() ?? '(none)',
      vhsStats: vhs?.stats ? {
        bandwidth: vhs.stats.bandwidth,
        mediaRequests: vhs.stats.mediaRequests,
        mediaRequestsErrored: vhs.stats.mediaRequestsErrored,
        mediaRequestsTimedout: vhs.stats.mediaRequestsTimedout,
        mediaRequestsAborted: vhs.stats.mediaRequestsAborted,
        mediaBytesTransferred: vhs.stats.mediaBytesTransferred,
      } : null,
      masterPlaylistLoaded: Boolean(vhs?.playlists?.master || vhs?.playlists?.main),
      mediaPlaylistLoaded: Boolean(vhs?.playlists?.media?.()),
      playlistLoaderState: vhs?.playlists?.state ?? '(n/a)',
    }
  } catch (e) {
    return { techStateError: String(e) }
  }
}

export function environment(): Record<string, unknown> {
  const anyWin = window as any
  const MS = anyWin.MediaSource
  const MMS = anyWin.ManagedMediaSource
  const probe = (type: string) => {
    try { return MS?.isTypeSupported?.(type) ?? false } catch { return 'threw' }
  }
  return {
    userAgent: navigator.userAgent,
    origin: window.location.origin,
    secureContext: window.isSecureContext,
    hasMediaSource: Boolean(MS),
    hasManagedMediaSource: Boolean(MMS),
    // The two codec strings a Bunny HLS ladder normally needs. If MSE exists
    // but says no to these, VHS can attach and then silently starve.
    'MSE avc1.4d401f': probe('video/mp4; codecs="avc1.4d401f"'),
    'MSE mp4a.40.2': probe('audio/mp4; codecs="mp4a.40.2"'),
    'MSE mp2t avc1': probe('video/mp2t; codecs="avc1.4d401f,mp4a.40.2"'),
    canPlayNativeHls: document.createElement('video').canPlayType('application/vnd.apple.mpegurl'),
    canPlayMpegUrl: document.createElement('video').canPlayType('application/x-mpegURL'),
  }
}

// Fetch the playlist ourselves, exactly as the player would, and report what
// comes back. This separates "the CDN refused us" from "the player never
// asked" — the console Network tab showed an empty body but not the reason.
async function probeOne(label: string, url: string): Promise<string | null> {
  try {
    const resp = await fetch(url, { cache: 'no-store' })
    const headers: Record<string, string> = {}
    resp.headers.forEach((v, k) => { headers[k] = v })
    const text = await resp.text()
    log(`probe ${label}`, {
      url,
      status: resp.status,
      type: resp.type,
      bodyLength: text.length,
      contentType: headers['content-type'] ?? '(none)',
      allowOrigin: headers['access-control-allow-origin'] ?? '(none)',
    })
    return text
  } catch (e) {
    log(`probe ${label} THREW`, { url, name: (e as Error)?.name, message: (e as Error)?.message })
    return null
  }
}

// Walk the ladder the way VHS has to: master → a variant playlist → the
// separate audio playlist → the first media segment. The master fetching fine
// proves nothing if the child playlists or segments are the ones failing, and
// this ladder is demuxed (audio lives in its own rendition), so BOTH children
// must succeed before a single frame can decode.
export async function probePlaylist(src: string): Promise<void> {
  if (!enabled || !src) return
  const master = await probeOne('master', src)
  if (!master) return
  log('master body:\n' + master.slice(0, 2000))

  const base = src.slice(0, src.lastIndexOf('/') + 1)
  const abs = (u: string) => (/^https?:\/\//.test(u) ? u : base + u)

  const audioMatch = master.match(/TYPE=AUDIO[^\n]*URI="([^"]+)"/)
  const variants = master.split('\n').filter((l) => l.trim() && !l.startsWith('#'))
  // The last STREAM-INF line is the lowest-bandwidth rendition — the one VHS
  // would realistically start on, and the cheapest to prove end-to-end.
  const variantUri = variants.length ? variants[variants.length - 1].trim() : null

  if (audioMatch) await probeOne('audio playlist', abs(audioMatch[1]))
  if (!variantUri) return

  const variantUrl = abs(variantUri)
  const variant = await probeOne('variant playlist', variantUrl)
  if (!variant) return
  log('variant body:\n' + variant.slice(0, 1200))

  const vBase = variantUrl.slice(0, variantUrl.lastIndexOf('/') + 1)
  const segment = variant.split('\n').find((l) => l.trim() && !l.startsWith('#'))
  if (segment) {
    const segUrl = /^https?:\/\//.test(segment.trim()) ? segment.trim() : vBase + segment.trim()
    await probeOne('first segment', segUrl)
  }
}

// Log every HTTP request VHS makes and how it ends. This is the crux: the
// element sits at networkState=LOADING with no bytes and no error, so the
// question is whether VHS is asking for the variant/audio playlists and
// segments at all, asking and getting nothing, or never asking.
//
// Installed once globally — videojs.Vhs.xhr is a module-level singleton, so a
// second core must not stack another hook on top.
let vhsHookInstalled = false
function installVhsRequestLogging(): void {
  if (!enabled || vhsHookInstalled) return
  const Vhs = (videojs as any).Vhs
  if (!Vhs?.xhr) { log('VHS xhr hook unavailable — videojs.Vhs.xhr missing') ; return }
  vhsHookInstalled = true

  // onRequest/onResponse are the supported hooks; the older `beforeRequest`
  // property still works but logs a deprecation warning on every single
  // request, which drowns the log this exists to produce.
  if (typeof Vhs.xhr.onRequest !== 'function' || typeof Vhs.xhr.onResponse !== 'function') {
    log('VHS request logging unavailable — onRequest/onResponse hooks missing')
    return
  }

  let seq = 0
  const started = new Map<string, { id: number; at: number }>()

  Vhs.xhr.onRequest = (options: Record<string, unknown>) => {
    const id = ++seq
    const uri = String(options?.uri || options?.url || '(no uri)')
    started.set(uri, { id, at: Date.now() })
    log(`vhs REQUEST #${id}`, {
      uri,
      method: options?.method || 'GET',
      range: (options?.headers as Record<string, string> | undefined)?.Range ?? '(none)',
      timeout: options?.timeout,
    })
    return options
  }

  Vhs.xhr.onResponse = (request: any, error: any, response: any) => {
    const uri = String(request?.uri || response?.url || '(no uri)')
    const start = started.get(uri)
    started.delete(uri)
    log(`vhs RESPONSE #${start?.id ?? '?'}`, {
      uri,
      ms: start ? Date.now() - start.at : '(unknown)',
      error: error ? `${error.code ?? ''} ${error.message ?? String(error)}` : null,
      status: response?.statusCode ?? request?.status ?? '(none)',
      bytes: response?.body?.byteLength ?? response?.body?.length ?? 0,
    })
  }

  log('VHS request logging installed')
}

export interface DiagnosticsHandle {
  dump(label: string): void
  // Follow the tech when it substitutes its own media element (iOS), so the
  // log describes the element that is actually playing rather than the
  // discarded original.
  retarget(next: HTMLVideoElement): void
  dispose(): void
}

// Attach to a live core. Returns a handle so PlaybackCore can dump state at
// interesting moments (setSource, play, play-rejection) and detach on unmount.
export function attachMediaDiagnostics(player: Player, el: HTMLVideoElement, initialSrc: string): DiagnosticsHandle {
  if (!enabled) {
    return { dump: () => {}, retarget: () => {}, dispose: () => {} }
  }

  // The element under observation. Mutable: the tech can replace it (iOS).
  let current = el

  installVhsRequestLogging()
  log('environment', environment())
  log('initial element state', elementState(current))
  void probePlaylist(initialSrc)

  // Two flat lines rather than one nested object, so both survive a paste.
  const dump = (label: string) => {
    log(`STATE[el] @ ${label}`, elementState(current))
    log(`STATE[tech] @ ${label}`, techState(player, current))
  }

  const noisyCount: Record<string, number> = {}
  const listeners: Array<[string, EventListener]> = []
  for (const name of MEDIA_EVENTS) {
    const fn = () => {
      if (NOISY.has(name)) {
        noisyCount[name] = (noisyCount[name] || 0) + 1
        // One in ten is enough to prove the pipeline is alive without flooding.
        if (noisyCount[name] % 10 !== 1) return
        log(`event(current) ${name} #${noisyCount[name]}`, { readyState: current.readyState, currentTime: Number(current.currentTime.toFixed(2)) })
        return
      }
      if (name === 'error') {
        log('event(current) error', { error: current.error ? { code: current.error.code, message: current.error.message } : null, ...elementState(current) })
        return
      }
      log(`event(current) ${name}`, { readyState: READY_STATE[current.readyState], networkState: NETWORK_STATE[current.networkState] })
    }
    current.addEventListener(name, fn)
    listeners.push([name, fn])
  }

  // video.js-level errors carry a different (often more specific) message than
  // the raw element's MediaError.
  const onPlayerError = () => {
    const err = player.error?.()
    log('event(vjs) error', { code: err?.code, message: err?.message, tech: JSON.stringify(techState(player, current)) })
  }
  player.on('error', onPlayerError)

  // VHS emits its own retry/blacklist chatter that never surfaces as a player
  // error — this is where a stalled-but-not-failed load shows itself.
  const onLoadedSource = () => {
    const vhs = (player as any).tech?.(true)?.vhs
    log('vjs loadedsource', techState(player, current))
    if (!vhs) return
    for (const ev of ['error', 'retryplaylist', 'usage', 'mediachange']) {
      try {
        vhs.playlists?.on?.(ev, () => log(`vhs.playlists ${ev}`, { state: vhs.playlists?.state, error: vhs.playlists?.error }))
      } catch { /* older/newer VHS shapes differ */ }
    }
  }
  player.on('loadedmetadata', onLoadedSource)
  player.on('loadstart', onLoadedSource)

  // A heartbeat: the failure mode is silence, so absence of events is the
  // signal. Periodic dumps make the stall visible and show whether any counter
  // (bytes, requests, readyState) is moving at all.
  let ticks = 0
  const interval = window.setInterval(() => {
    ticks++
    dump(`heartbeat ${ticks}`)
    if (ticks >= 12) window.clearInterval(interval)
  }, 2000)

  return {
    dump,
    retarget: (next: HTMLVideoElement) => {
      if (next === current) return
      for (const [name, fn] of listeners) current.removeEventListener(name, fn)
      const previous = current
      current = next
      for (const [name, fn] of listeners) current.addEventListener(name, fn)
      log('RETARGETED to the tech element', {
        previousInDocument: previous.isConnected,
        ...elementState(current),
      })
    },
    dispose: () => {
      window.clearInterval(interval)
      for (const [name, fn] of listeners) current.removeEventListener(name, fn)
      try {
        player.off('error', onPlayerError)
        player.off('loadedmetadata', onLoadedSource)
        player.off('loadstart', onLoadedSource)
      } catch { /* player may already be disposed */ }
    },
  }
}
